// src/Components/Promo/NewsfeedDownload.js

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import DownloadIcon from '@mui/icons-material/Download';
import CssBaseline from '@mui/material/CssBaseline';

export default function NewsfeedDownload({ logoUrl }) {
  const [searchParams] = useSearchParams();
  const docId = searchParams.get('id');
  const [loading, setLoading] = useState(true);
  const [newsfeedData, setNewsfeedData] = useState(null);
  const [error, setError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!docId) {
      setError('Invalid link. No document ID provided.');
      setLoading(false);
      return;
    }

    const fetchDoc = async () => {
      try {
        const docRef = doc(db, 'newsPortfolio', docId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setNewsfeedData({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError('Document not found or has been removed.');
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load document.');
      } finally {
        setLoading(false);
      }
    };
    fetchDoc();
  }, [docId]);

  const handleDownload = () => {
    if (!newsfeedData || !newsfeedData.pdfDownloadUrl) return;
    setIsDownloading(true);

    const storageKey = `loktr_downloaded_${docId}`;
    const hasDownloaded = localStorage.getItem(storageKey);

    if (hasDownloaded) {
      // User has clicked this before. Route directly to the file to prevent duplicate hits.
      window.location.href = newsfeedData.pdfDownloadUrl;
      return;
    }

    // First time click. Mark the browser storage and route to the Cloud Function counter.
    localStorage.setItem(storageKey, 'true');

    // Construct the secure Cloud Function target string
    const projectId = db.app.options.projectId;
    const targetLink = `https://australia-southeast1-${projectId}.cloudfunctions.net/trackNewsfeedClick?id=${docId}`;

    // Divert the window to the server-side Cloud Function logic
    window.location.href = targetLink;
  };

  if (loading) {
    return (
      <React.Fragment>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <CircularProgress />
        </Box>
      </React.Fragment>
    );
  }

  if (error) {
    return (
      <React.Fragment>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}>
          <Typography color='error' variant='h6'>
            {error}
          </Typography>
        </Box>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <CssBaseline />
      <Box
        sx={{
          bgcolor: '#f4f4f4',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}>
        {logoUrl && (
          <Box sx={{ mb: 4 }}>
            <img src={logoUrl} alt='Logo' style={{ maxHeight: '80px', width: 'auto' }} />
          </Box>
        )}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
          }}>
          <Typography variant='h5' sx={{ color: '#00008B', fontWeight: 'bold', mb: 2 }}>
            {newsfeedData.subjectTitle || newsfeedData.name || 'Newsfeed Document'}
          </Typography>

          {newsfeedData.pdfThumbnailUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <img
                src={newsfeedData.pdfThumbnailUrl}
                alt='Thumbnail'
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  height: 'auto',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                }}
              />
            </Box>
          )}

          <Button
            variant='contained'
            startIcon={
              isDownloading ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={handleDownload}
            disabled={isDownloading}
            sx={{
              bgcolor: '#0056b3',
              '&:hover': { bgcolor: '#004494' },
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              mt: 2,
            }}>
            {isDownloading ? 'Opening...' : 'Download PDF'}
          </Button>
        </Paper>
      </Box>
    </React.Fragment>
  );
}
